import React, { useEffect, useState } from 'react';
import { FlaskConical, AlertCircle, Plus, X, Save, Paperclip, ChevronDown, ChevronRight } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import PatientDropdown from '../components/PatientDropdown';
import LabDocumentAttachments from '../components/LabDocumentAttachments';

function Labs({ user }) {
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    testName: '',
    loincCode: '',
    resultValue: '',
    resultUnit: '',
    referenceRange: '',
    isAbnormal: false,
    specimenDate: new Date().toISOString().split('T')[0],
    resultDate: '',
    status: 'pending',
    notes: '',
  });

  useEffect(() => {
    async function load() {
      try {
        const data = await window.zignagenixAPI.getPatients();
        setPatients(Array.isArray(data) ? data : []);
        if (Array.isArray(data) && data.length > 0) {
          setSelectedPatientId(data[0].id);
          const labData = await window.zignagenixAPI.getLabResults(data[0].id);
          setLabs(Array.isArray(labData) ? labData : []);
        }
      } catch (err) {
        console.error('Failed to load:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handlePatientChange(patientId) {
    setSelectedPatientId(patientId);
    try {
      const labData = await window.zignagenixAPI.getLabResults(patientId);
      setLabs(Array.isArray(labData) ? labData : []);
    } catch (err) {
      console.error('Failed to load labs:', err);
    }
  }

  const resetForm = () => {
    setForm({
      testName: '',
      loincCode: '',
      resultValue: '',
      resultUnit: '',
      referenceRange: '',
      isAbnormal: false,
      specimenDate: new Date().toISOString().split('T')[0],
      resultDate: '',
      status: 'pending',
      notes: '',
    });
    setError(null);
  };

  const handleSubmit = async () => {
    if (!form.testName || !form.specimenDate) {
      setError('Test name and specimen date are required');
      return;
    }
    try {
      await window.zignagenixAPI.createLabResult({
        patientId: selectedPatientId,
        ...form,
      });
      const labData = await window.zignagenixAPI.getLabResults(selectedPatientId);
      setLabs(Array.isArray(labData) ? labData : []);
      setShowForm(false);
      resetForm();
    } catch (err) {
      setError('Failed to save lab result');
      console.error(err);
    }
  };

  const hasCreatePermission = user?.permissions?.includes('labs:create');
  const hasDeletePermission = user?.permissions?.includes('labs:create');   // server gates delete on labs:create too

  // Track which lab rows have their attachments expanded
  const [expandedLabIds, setExpandedLabIds] = useState(new Set());
  const toggleExpand = (id) => {
    setExpandedLabIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) return <div className="page-loading">Loading lab results...</div>;

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Lab Results</h1>
        <PrintButton pageTitle="Lab Results" />
      </div>

      <div className="filter-bar">
        <label className="filter-label">Patient:</label>
        <PatientDropdown
          patients={patients}
          value={String(selectedPatientId)}
          onChange={val => handlePatientChange(Number(val))}
          placeholder="Select a patient"
          idPrefix="labs"
        />

        {hasCreatePermission && selectedPatientId && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={18} /> New Lab Result
          </button>
        )}
      </div>

      {showForm && (
        <div className="form-section">
          <h2 className="form-section-title">Enter Lab Result</h2>

          {error && (
            <div className="alert alert--error">{error}</div>
          )}

          <div className="patient-form-grid">
            <div className="form-group">
              <label className="form-label">Test Name *</label>
              <input type="text" className="form-input" value={form.testName}
                onChange={(e) => setForm(prev => ({ ...prev, testName: e.target.value }))}
                placeholder="e.g., Complete Blood Count" />
            </div>
            <div className="form-group">
              <label className="form-label">LOINC Code</label>
              <input type="text" className="form-input" value={form.loincCode}
                onChange={(e) => setForm(prev => ({ ...prev, loincCode: e.target.value }))}
                placeholder="e.g., 58410-2" />
            </div>
            <div className="form-group">
              <label className="form-label">Result Value</label>
              <input type="text" className="form-input" value={form.resultValue}
                onChange={(e) => setForm(prev => ({ ...prev, resultValue: e.target.value }))}
                placeholder="e.g., 14.2" />
            </div>
            <div className="form-group">
              <label className="form-label">Result Unit</label>
              <input type="text" className="form-input" value={form.resultUnit}
                onChange={(e) => setForm(prev => ({ ...prev, resultUnit: e.target.value }))}
                placeholder="e.g., g/dL" />
            </div>
            <div className="form-group">
              <label className="form-label">Reference Range</label>
              <input type="text" className="form-input" value={form.referenceRange}
                onChange={(e) => setForm(prev => ({ ...prev, referenceRange: e.target.value }))}
                placeholder="e.g., 12.0-17.5" />
            </div>
            <div className="form-group">
              <label className="form-label">Specimen Date *</label>
              <input type="date" className="form-input" value={form.specimenDate}
                onChange={(e) => setForm(prev => ({ ...prev, specimenDate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Result Date</label>
              <input type="date" className="form-input" value={form.resultDate}
                onChange={(e) => setForm(prev => ({ ...prev, resultDate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status}
                onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}>
                <option value="pending">Pending</option>
                <option value="preliminary">Preliminary</option>
                <option value="final">Final</option>
                <option value="corrected">Corrected</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '24px' }}>
              <input type="checkbox" id="isAbnormal" className="form-checkbox" checked={form.isAbnormal}
                onChange={(e) => setForm(prev => ({ ...prev, isAbnormal: e.target.checked }))} />
              <label htmlFor="isAbnormal" className="form-label" style={{ marginBottom: 0 }}>Abnormal Result</label>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '12px' }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes..." rows="2" />
          </div>

          <div className="form-actions">
            <button className="btn-primary" onClick={handleSubmit}>
              <Save size={18} /> Save Result
            </button>
            <button className="btn-ghost" onClick={() => { setShowForm(false); resetForm(); }}>
              <X size={18} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Date</th>
              <th>Test</th>
              <th>LOINC</th>
              <th>Result</th>
              <th>Unit</th>
              <th>Reference</th>
              <th>Status</th>
              <th>Flag</th>
              <th>Docs</th>
            </tr>
          </thead>
          <tbody>
            {labs.map((lab) => {
              const isExpanded = expandedLabIds.has(lab.id);
              return (
                <React.Fragment key={lab.id}>
                  <tr className={lab.is_abnormal ? 'row--abnormal' : ''}>
                    <td>
                      <button
                        onClick={() => toggleExpand(lab.id)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#5a6478' }}
                        title={isExpanded ? 'Hide attachments' : 'Show / upload attachments'}
                        aria-label={isExpanded ? 'Collapse attachments' : 'Expand attachments'}
                      >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </td>
                    <td>{lab.specimen_date}</td>
                    <td><FlaskConical size={14} /> {lab.test_name}</td>
                    <td className="mono">{lab.loinc_code || '—'}</td>
                    <td className="mono">{lab.result_value}</td>
                    <td>{lab.result_unit || '—'}</td>
                    <td>{lab.reference_range || '—'}</td>
                    <td><span className={`status-badge status-badge--${lab.status}`}>{lab.status}</span></td>
                    <td>{lab.is_abnormal ? <AlertCircle size={16} className="icon--red" /> : '—'}</td>
                    <td>
                      <button
                        onClick={() => toggleExpand(lab.id)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: isExpanded ? '#1f6feb' : '#5a6478', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        title="Manage attached documents"
                      >
                        <Paperclip size={14} />
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan="10" style={{ padding: 0 }}>
                        <LabDocumentAttachments
                          labResultId={lab.id}
                          canUpload={hasCreatePermission}
                          canDelete={hasDeletePermission}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {labs.length === 0 && (
              <tr><td colSpan="10" className="empty-state">No lab results</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Labs;
